import { useEffect, useState } from 'react'
import { Box, Button, Divider, TextField, Typography } from '@mui/material'
import { ReferralVerificationData } from './types'

type Center = NonNullable<ReferralVerificationData['center']>

export interface ResourceInfoFormValues {
  altName: string
  websiteAddress: string
  phoneNumber: string
  faxNumber: string
  emailAddress: string
  streetAddress: string
  city: string
  state: string
  zipCode: string
  applicationProcedure: string
  eligibility: string
  feeScale: string
  websiteNotes: string
  description: string
}

export const toResourceInfoFormValues = (
  center: Center
): ResourceInfoFormValues => ({
  altName: center.altName || '',
  websiteAddress: center.websiteAddress || '',
  phoneNumber: center.phoneNumber || '',
  faxNumber: center.faxNumber || '',
  emailAddress: center.emailAddress || '',
  streetAddress: center.streetAddress || '',
  city: center.city || '',
  state: center.state || '',
  zipCode: center.zipCode || '',
  applicationProcedure: center.applicationProcedure || '',
  eligibility: center.eligibility || '',
  feeScale: center.feeScale || '',
  websiteNotes: center.websiteNotes || '',
  description: center.description || '',
})

const FIELD_LABELS: { key: keyof ResourceInfoFormValues; label: string }[] = [
  { key: 'altName', label: 'Alternate Name' },
  { key: 'websiteAddress', label: 'Website / URL' },
  { key: 'phoneNumber', label: 'Phone' },
  { key: 'faxNumber', label: 'Fax' },
  { key: 'emailAddress', label: 'Email' },
  { key: 'streetAddress', label: 'Street Address' },
  { key: 'city', label: 'City' },
  { key: 'state', label: 'State' },
  { key: 'zipCode', label: 'Zip Code' },
  { key: 'applicationProcedure', label: 'Application Procedure' },
  { key: 'eligibility', label: 'Eligibility' },
  { key: 'feeScale', label: 'Fee Scale' },
  { key: 'websiteNotes', label: 'Web Notes' },
  { key: 'description', label: 'Description' },
]

export interface ResourceInfoSectionProps {
  center: Center
  onChange?: (values: ResourceInfoFormValues) => void
}

export const ResourceInfoSection = ({
  center,
  onChange,
}: ResourceInfoSectionProps): JSX.Element => {
  const [isEditing, setIsEditing] = useState(false)
  const [values, setValues] = useState<ResourceInfoFormValues>(
    toResourceInfoFormValues(center)
  )

  useEffect(() => {
    const initial = toResourceInfoFormValues(center)
    setValues(initial)
    onChange?.(initial)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [center])

  const handleFieldChange = (
    key: keyof ResourceInfoFormValues,
    value: string
  ): void => {
    setValues((prev) => {
      const next = { ...prev, [key]: value }
      onChange?.(next)
      return next
    })
  }

  return (
    <Box>
      <Box
        display="flex"
        justifyContent="space-between"
        alignItems="center"
        mb={2}
      >
        <Typography variant="subtitle1" fontWeight="bold" color="primary">
          Resource Information
        </Typography>
        <Button
          size="small"
          variant="outlined"
          onClick={() => setIsEditing((prev) => !prev)}
        >
          {isEditing ? 'Done' : 'Edit'}
        </Button>
      </Box>
      <Divider sx={{ mb: 2 }} />

      <Box display="flex" flexDirection="column" gap={2}>
        <Box>
          <Typography variant="caption" color="text.secondary">
            Resource Name
          </Typography>
          <Typography variant="body1">{center.name}</Typography>
        </Box>

        {FIELD_LABELS.map(({ key, label }) =>
          isEditing ? (
            <TextField
              key={key}
              fullWidth
              size="small"
              label={label}
              value={values[key]}
              multiline={
                key === 'applicationProcedure' ||
                key === 'websiteNotes' ||
                key === 'description'
              }
              onChange={(e) => handleFieldChange(key, e.target.value)}
            />
          ) : (
            values[key] && (
              <Box key={key}>
                <Typography variant="caption" color="text.secondary">
                  {label}
                </Typography>
                <Typography variant="body1">{values[key]}</Typography>
              </Box>
            )
          )
        )}
      </Box>
    </Box>
  )
}
